import type { DashboardFreeSlotsDto, MeDto } from '@bqcrm/shared';
import { Anchor, Badge, Group, Select, Stack, Text } from '@mantine/core';
import { IconCalendarEvent } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, HttpError } from '../../api';
import { canModule } from '../../auth';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { dayLong, dayRange, plural } from '../../format';

const DAYS = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} ${plural(n, 'dzień', 'dni', 'dni')}` }));
const KEY = 'pulpit.wolne_terminy';

/** Ostatnio wybrana branża i długość prac — tylko wygoda na tym komputerze. */
const load = (): { trade: string | null; days: string } => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { trade?: string | null; days?: string } | null;
    return { trade: v?.trade ?? null, days: DAYS.some((d) => d.value === v?.days) ? v!.days! : '1' };
  } catch {
    return { trade: null, days: '1' };
  }
};

/**
 * Pulpit, „Najbliższe wolne terminy” (docs/20 — te same reguły co w sprzedaży w terenie): branże z przydziału pracownika;
 * koordynator branży widzi ekipy, handlowiec — same dni i liczbę wolnych ekip.
 */
export function FreeSlotsCard({ me }: { me: MeDto }) {
  const [pick, setPick] = useState(load);
  const set = (v: Partial<typeof pick>) =>
    setPick((p) => {
      const next = { ...p, ...v };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        // bez pamięci przeglądarki — wybór tylko do odświeżenia strony
      }
      return next;
    });
  const q = useQuery({
    queryKey: ['dashboard-free-slots', pick.trade, pick.days],
    queryFn: () => api<DashboardFreeSlotsDto>(`/dashboard/free-slots?days=${pick.days}${pick.trade ? `&trade=${encodeURIComponent(pick.trade)}` : ''}`),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  // zapamiętana branża, której już nie ma w przydziale — wracamy do pierwszej
  const lost = q.error instanceof HttpError && q.error.status === 403 && !!pick.trade;
  useEffect(() => {
    if (lost) set({ trade: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lost]);
  const d = q.data;
  return (
    <SectionCard
      title="Najbliższe wolne terminy"
      icon={<IconCalendarEvent size={16} />}
      padding="sm"
      actions={
        d?.crews && canModule(me, 'planowanie') ? (
          <Anchor component={Link} to="/planowanie" size="sm">
            Plan
          </Anchor>
        ) : undefined
      }
    >
      <QueryState isLoading={q.isLoading} error={lost ? null : q.error} lines={3}>
        {d && !d.trades.length ? (
          <EmptyState compact icon={<IconCalendarEvent size={18} />} title="Nie masz przydzielonej branży" hint="Wolne terminy pokazują się dla branż, w których prowadzisz oferty albo planujesz prace — przydział ustawia administrator w karcie pracownika." />
        ) : d ? (
          <Stack gap="xs">
            <Group gap="xs" wrap="nowrap" px={4}>
              {d.trades.length > 1 && (
                <Select
                  size="xs"
                  aria-label="Branża"
                  data={d.trades.map((t) => ({ value: t.code, label: t.name }))}
                  value={d.tradeCode}
                  onChange={(v) => v && set({ trade: v })}
                  allowDeselect={false}
                  style={{ flex: 1, minWidth: 0 }}
                />
              )}
              {d.trades.length === 1 && (
                <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0 }} truncate>
                  {d.trades[0]!.name}
                </Text>
              )}
              <Select size="xs" aria-label="Długość prac" data={DAYS} value={pick.days} onChange={(v) => v && set({ days: v })} allowDeselect={false} w={96} />
            </Group>
            {d.slots.length ? (
              <Stack gap={2}>
                {d.slots.map((s, i) => (
                  <Group key={`${s.dateFrom}|${s.teamId ?? i}`} wrap="nowrap" gap="sm" px="sm" py={6}>
                    <Badge variant="light" color={i === 0 ? 'teal' : 'gray'} w={92} style={{ flexShrink: 0 }}>
                      {i === 0 ? 'najbliższy' : `${i + 1}.`}
                    </Badge>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>
                        {s.dateFrom === s.dateTo ? dayLong(s.dateFrom, { year: false }) : dayRange(s.dateFrom, s.dateTo)}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {s.teamName ?? `${s.crews} ${plural(s.crews, 'wolna ekipa', 'wolne ekipy', 'wolnych ekip')}`}
                      </Text>
                    </div>
                  </Group>
                ))}
                {!d.crews && (
                  <Text size="xs" c="dimmed" px="sm" pt={4}>
                    Termin rezerwuje koordynator — ekipy i kalendarze widzi on.
                  </Text>
                )}
              </Stack>
            ) : (
              <EmptyState compact icon={<IconCalendarEvent size={18} />} title="Brak wolnych terminów w najbliższych 60 dniach" hint="Ekipy tej branży są zajęte albo nie mają składu — sprawdź krótszą długość prac." />
            )}
          </Stack>
        ) : null}
      </QueryState>
    </SectionCard>
  );
}
